import { Component } from '@theme/component';

/**
 * @typedef {Object} FilterSearchRefs
 * @property {HTMLInputElement} input - The text field the shopper types into
 * @property {HTMLElement} status - Live region announcing (and showing) the "no matches" message
 */

/**
 * Narrows a long filter list (brands, colors) as the shopper types.
 *
 * Client-side only: the field has no `name`, so it never reaches the facets form
 * data and never triggers a section render. Items are matched against their
 * `data-search` attribute and hidden with a class, leaving the checkbox inputs
 * (and their checked state) untouched.
 *
 * @extends {Component<FilterSearchRefs>}
 */
class FilterSearchComponent extends Component {
  requiredRefs = ['input', 'status'];

  connectedCallback() {
    super.connectedCallback();
    // A section re-render can restore the typed text (the input is skipped by the morph), so re-apply it.
    if (this.refs.input.value) this.filter();
  }

  /**
   * Hides the list items that don't match the typed text
   */
  filter() {
    const { input, status } = this.refs;
    const query = normalize(input.value);
    const items = this.#items();

    let matches = 0;
    for (const item of items) {
      const isMatch = query === '' || normalize(item.dataset.search ?? '').includes(query);
      item.classList.toggle('facets__inputs-list-item--no-match', !isMatch);
      if (isMatch) matches++;
    }

    status.textContent = query !== '' && matches === 0 ? (this.dataset.emptyText ?? '') : '';
  }

  /**
   * Enter must not submit the facets form. Escape clears the text first and only then closes the panel.
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
    } else if (event.key === 'Escape' && this.refs.input.value !== '') {
      event.preventDefault();
      event.stopPropagation();
      this.refs.input.value = '';
      this.filter();
    }
  }

  /**
   * @returns {NodeListOf<HTMLElement>}
   */
  #items() {
    const panel = this.closest('floating-panel-component, .facets__panel-content') ?? this.parentElement;
    return panel?.querySelectorAll('.facets__inputs-list-item') ?? /** @type {NodeListOf<HTMLElement>} */ ([]);
  }
}

/**
 * Lowercases and strips diacritics so "Café" matches "cafe".
 * @param {string} text
 * @returns {string}
 */
function normalize(text) {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

if (!customElements.get('filter-search-component')) {
  customElements.define('filter-search-component', FilterSearchComponent);
}
